import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | 'legal' | null;
const modalContent: Record<Exclude<ModalType, null>, {
  title: string;
  body: string;
}> = {
  privacy: {
    title: 'Política de Privacidad',
    body: `Protocolo Zenith ("nosotros", "nuestro") se compromete a proteger tu privacidad. Esta política describe cómo recopilamos, usamos y protegemos tu información personal.

Información que Recopilamos:
• Información de contacto (nombre, correo electrónico) proporcionada voluntariamente al realizar una compra.
• Información de pago procesada de forma segura a través de nuestros proveedores de pago. No almacenamos datos de tarjetas de crédito.
• Datos de uso del sitio web recopilados mediante cookies y tecnologías similares.

Uso de la Información:
• Para procesar tu compra y entregar el producto digital.
• Para enviarte comunicaciones relacionadas con tu compra.
• Para mejorar nuestro sitio web y experiencia del usuario.

No vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.

Tus Derechos:
• Puedes solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento escribiéndonos a protocolzenith@outlook.com.

Esta política puede actualizarse periódicamente. La fecha de última actualización se indicará al inicio de este documento.

Última actualización: Enero 2025.`
  },
  terms: {
    title: 'Términos de Servicio',
    body: `Al comprar y/o acceder al Ebook "Protocolo Zenith", aceptas los siguientes términos y condiciones:

Naturaleza del Producto:
• Protocolo Zenith es un producto 100% educativo en formato digital (ebook PDF).
• NO somos una agencia de reparación de crédito.
• NO prometemos ni garantizamos mejoras específicas en tu puntaje crediticio.
• El contenido es puramente informativo y educativo. Los resultados dependen exclusivamente de tus acciones.

Entrega del Producto:
• El ebook se entrega de forma digital e inmediata tras confirmar el pago.
• Recibirás acceso al archivo descargable en tu correo electrónico.

Política de Reembolso:
• Ofrecemos una garantía de devolución de 30 días. Si no estás satisfecho, puedes solicitar un reembolso completo escribiendo a protocolzenith@outlook.com dentro de los 30 días posteriores a tu compra.

Propiedad Intelectual:
• Todo el contenido del ebook está protegido por derechos de autor. Queda prohibida su reproducción, distribución o reventa sin autorización expresa.

Limitación de Responsabilidad:
• Protocolo Zenith no se hace responsable de los resultados obtenidos por el usuario. El contenido es educativo y no constituye asesoría financiera, legal o crediticia profesional.

Contacto: protocolzenith@outlook.com`
  },
  legal: {
    title: 'Aviso Legal',
    body: `AVISO LEGAL — PROTOCOLO ZENITH

Protocolo Zenith es un producto digital de carácter exclusivamente educativo.

Declaraciones Importantes:
• Este ebook NO constituye asesoría financiera, legal ni crediticia profesional.
• NO somos una agencia de reparación de crédito ni actuamos como tal.
• NO prometemos, garantizamos ni implicamos mejoras específicas en puntajes de crédito.
• La información contenida se basa en leyes y regulaciones públicas como la Fair Credit Reporting Act (FCRA) y se presenta con fines educativos.

Responsabilidad del Usuario:
• El lector es el único responsable de las acciones que tome basándose en la información del ebook.
• Recomendamos consultar con un profesional financiero o legal antes de tomar decisiones importantes sobre tu crédito.

Resultados:
• Los testimonios o ejemplos mencionados, si los hubiera, no garantizan resultados similares.
• Cada situación crediticia es única y los resultados varían según las circunstancias individuales y el esfuerzo aplicado.

Jurisdicción:
• Este producto está dirigido a residentes de los Estados Unidos de América.
• Cualquier disputa se resolverá bajo las leyes aplicables del estado correspondiente.

Para preguntas o consultas: protocolzenith@outlook.com

© 2025 Protocolo Zenith. Todos los derechos reservados.`
  }
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-zenith-bg py-12 px-4 border-t border-white/5" data-id="element-14">
        <div className="max-w-4xl mx-auto text-center" data-id="element-15">
          {/* Educational Disclaimer */}
          <div className="bg-zenith-card/50 border border-white/5 p-6 rounded-lg mb-8" data-id="element-16">
            <p className="text-xs md:text-sm text-zenith-muted leading-relaxed" data-id="element-17">
              <strong className="text-white/70" data-id="element-18">Aviso Importante:</strong> El
              Protocolo Zenith es un producto 100% educativo. No somos una
              agencia de reparación de crédito. No prometemos mejoras
              específicas de score. Enseñamos el proceso — tú lo ejecutas. Los
              resultados dependen de tu acción.
            </p>
          </div>

          {/* Legal Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6" data-id="element-19">
            <button onClick={() => setActiveModal('privacy')} className="text-xs md:text-sm text-zenith-muted hover:text-white transition-colors underline underline-offset-2 decoration-white/20 hover:decoration-white/60" data-id="element-20">
              Privacy Policy
            </button>
            <span className="text-zenith-muted/30 text-xs" data-id="element-21">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-xs md:text-sm text-zenith-muted hover:text-white transition-colors underline underline-offset-2 decoration-white/20 hover:decoration-white/60" data-id="element-22">
              Terms of Service
            </button>
            <span className="text-zenith-muted/30 text-xs" data-id="element-23">|</span>
            <button onClick={() => setActiveModal('legal')} className="text-xs md:text-sm text-zenith-muted hover:text-white transition-colors underline underline-offset-2 decoration-white/20 hover:decoration-white/60" data-id="element-24">
              Aviso Legal
            </button>
          </div>

          {/* Contact Email */}
          <a href="mailto:protocolzenith@outlook.com" className="inline-flex items-center gap-2 text-xs md:text-sm text-zenith-muted hover:text-zenith-red transition-colors mb-6" data-id="element-25">
            <Mail className="w-4 h-4" data-id="element-26" />
            protocolzenith@outlook.com
          </a>

          {/* Copyright */}
          <p className="text-xs text-zenith-muted/50" data-id="element-27">
            &copy; 2025 Protocolo Zenith. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Legal Modals */}
      <AnimatePresence data-id="element-28">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-29">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="relative w-full max-w-2xl max-h-[80vh] bg-zenith-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-30">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-31">
                <h3 className="text-xl font-bold text-white font-serif" data-id="element-32">
                  {modalContent[activeModal].title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-lg text-zenith-muted hover:text-white hover:bg-white/10 transition-colors" aria-label="Cerrar" data-id="element-33">
                  <X className="w-5 h-5" data-id="element-34" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-35">
                <p className="text-sm text-zenith-muted leading-relaxed whitespace-pre-line" data-id="element-36">
                  {modalContent[activeModal].body}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}